import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { fehlertext } from '@/lib/fehlertext'
import { documentsApi } from '../api/documents.api'
import { formatBetrag, formatDatum, leseZahl } from '../lib/grid'
import type { DocumentDetail } from '../types/document.types'
import { bestaetige } from '@/lib/dialoge'

const METHODEN = ['TRANSFER', 'DIRECT_DEBIT', 'CASH', 'CARD'] as const
const eingabe =
  'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

function heute(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * AP-32 8.4 — **Zahlungen am Beleg.** Datum, Betrag, Zahlungsart, Notiz und das
 * Kennzeichen Skonto; eine Rückzahlung ist ein negativer Betrag. Offener Betrag
 * und Status rechnet der Server bei jedem Schreiben neu.
 */
export function PaymentsBlock({ beleg, onChange }: { beleg: DocumentDetail; onChange: (b: DocumentDetail) => void }) {
  const { t } = useTranslation()
  const traegt = beleg.capabilities.payable && beleg.kind !== 'CANCELLATION' && beleg.kind !== 'CREDIT_NOTE'
  const offen = beleg.openAmount ?? 0
  const [datum, setDatum] = useState(heute)
  const [betrag, setBetrag] = useState('')
  const [methode, setMethode] = useState<(typeof METHODEN)[number]>('TRANSFER')
  const [skonto, setSkonto] = useState(false)
  const [notiz, setNotiz] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const zahlungen = beleg.payments ?? []
  if (!traegt && zahlungen.length === 0) return null
  const erfassbar = traegt && beleg.status !== 'DRAFT' && beleg.rights.payments

  const mitLauf = async (aktion: () => Promise<void>) => {
    setFehler(null)
    setLaeuft(true)
    try {
      await aktion()
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setLaeuft(false)
    }
  }

  const erfassen = () =>
    mitLauf(async () => {
      const wert = leseZahl(betrag)
      if (wert === null || Number.isNaN(wert) || wert === 0) {
        setFehler(t('documents.payments.amountInvalid'))
        return
      }
      const { data } = await documentsApi.addPayment(beleg.id, { paidOn: datum, amount: wert, method: methode, isDiscount: skonto, note: notiz || undefined })
      onChange(data.data)
      setBetrag('')
      setSkonto(false)
      setNotiz('')
    })

  const entfernen = (paymentId: string) =>
    mitLauf(async () => {
      if (!(await bestaetige(t('documents.payments.confirmRemove')))) return
      onChange((await documentsApi.removePayment(beleg.id, paymentId)).data.data)
    })

  return (
    <div className="space-y-2 md:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-muted-foreground">{t('documents.chain.payments')}</p>
        {traegt && beleg.status !== 'DRAFT' && beleg.openAmount !== undefined && (
          <p className="text-xs">
            {t('documents.payments.open', { amount: formatBetrag(offen, beleg.currency) })}
            {beleg.paymentStatus !== 'NONE' && ` · ${t(`documents.payStatuses.${beleg.paymentStatus}`)}`}
          </p>
        )}
      </div>

      {zahlungen.length === 0 ? (
        <p className="text-muted-foreground">{t('documents.payments.none')}</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {zahlungen.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0">
                <td className="py-1 pr-3 tabular-nums">{formatDatum(p.paidOn)}</td>
                <td className="py-1 pr-3">{t(`documents.payments.methods.${p.method}`, { defaultValue: p.method })}</td>
                <td className={`py-1 pr-3 text-right tabular-nums ${p.amount < 0 ? 'text-destructive' : ''}`}>
                  {formatBetrag(p.amount, beleg.currency)}
                  {p.isDiscount && <span className="ml-1 text-xs text-muted-foreground">({t('documents.payments.discount')})</span>}
                </td>
                <td className="py-1 pr-3 text-muted-foreground">{p.note ?? ''}</td>
                <td className="py-1 text-right">
                  {beleg.rights.payments && (
                    <button type="button" disabled={laeuft} onClick={() => void entfernen(p.id)} title={t('documents.payments.remove')} className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-40">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {erfassbar && (
        <div className="grid grid-cols-2 items-end gap-2 rounded-md border border-border/60 bg-muted/20 p-2 sm:grid-cols-6">
          <label className="space-y-0.5">
            <span className="block text-xs text-muted-foreground">{t('documents.payments.date')}</span>
            <input type="date" className={eingabe} value={datum} onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label className="space-y-0.5">
            <span className="block text-xs text-muted-foreground">{t('documents.payments.amount')}</span>
            <input
              inputMode="decimal"
              className={`${eingabe} text-right`}
              placeholder={offen > 0 ? formatBetrag(offen, beleg.currency) : ''}
              value={betrag}
              onChange={(e) => setBetrag(e.target.value)}
            />
          </label>
          <label className="space-y-0.5">
            <span className="block text-xs text-muted-foreground">{t('documents.payments.method')}</span>
            <select className={eingabe} value={methode} onChange={(e) => setMethode(e.target.value as (typeof METHODEN)[number])}>
              {METHODEN.map((m) => (
                <option key={m} value={m}>
                  {t(`documents.payments.methods.${m}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-0.5 sm:col-span-2">
            <span className="block text-xs text-muted-foreground">{t('documents.payments.note')}</span>
            <input className={eingabe} value={notiz} maxLength={500} onChange={(e) => setNotiz(e.target.value)} />
          </label>
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={skonto} onChange={(e) => setSkonto(e.target.checked)} />
              {t('documents.payments.isDiscount')}
            </label>
            <button type="button" disabled={laeuft || !betrag.trim()} onClick={() => void erfassen()} className="w-full rounded-md bg-primary px-2 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50">
              {t('documents.payments.add')}
            </button>
          </div>
          <p className="col-span-2 text-xs text-muted-foreground sm:col-span-6">{t('documents.payments.refundHint')}</p>
        </div>
      )}
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
    </div>
  )
}
